import { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Clock, Trash2, Plus, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { type TeamMember } from "./TeamPanel";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface HourLog {
  id: string;
  ba_name: string;
  requirement_id: string | null;
  hours: number;
  logged_date: string;
  note: string | null;
}

interface Props {
  projectId: string;
  baProjectId: string;
  team: TeamMember[];
}

// ─── Date helpers ──────────────────────────────────────────────────────────────

function getWeeksOfMonth(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const weeks: { start: Date; end: Date; label: string }[] = [];
  let cursor = new Date(firstDay);
  while (cursor.getDay() !== 1) cursor.setDate(cursor.getDate() - 1);
  while (cursor <= lastDay) {
    const start = new Date(cursor);
    const end = new Date(cursor);
    end.setDate(end.getDate() + 6);
    // Clamp using Date comparison, not day numbers, to avoid cross-month confusion
    const labelStart = start < firstDay ? firstDay : start;
    const labelEnd = end > lastDay ? lastDay : end;
    weeks.push({ start, end, label: `${labelStart.getDate()}–${labelEnd.getDate()}` });
    cursor.setDate(cursor.getDate() + 7);
  }
  return weeks;
}

function dateInRange(dateStr: string, start: Date, end: Date) {
  // Parse as local date to avoid UTC midnight vs local time mismatch
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return date >= startDay && date <= endDay;
}

function workdaysOff(timeOff: TeamMember["time_off"], weekStart: Date, weekEnd: Date): number {
  let days = 0;
  for (const to of timeOff) {
    const toStart = new Date(to.start);
    const toEnd = new Date(to.end);
    const overlapStart = new Date(Math.max(weekStart.getTime(), toStart.getTime()));
    const overlapEnd = new Date(Math.min(weekEnd.getTime(), toEnd.getTime()));
    if (overlapStart <= overlapEnd) {
      let d = new Date(overlapStart);
      while (d <= overlapEnd) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) days++;
        d.setDate(d.getDate() + 1);
      }
    }
  }
  return days;
}

function availableHours(member: TeamMember, weekStart: Date, weekEnd: Date) {
  const dailyHours = member.capacity_hours / 5;
  const offDays = workdaysOff(member.time_off, weekStart, weekEnd);
  return Math.max(0, (5 - offDays) * dailyHours);
}

// Neo-brutal load colors — small pill style
function loadStyle(logged: number, available: number): { bg: string; text: string; border: string } {
  if (available === 0) return { bg: "bg-[#f0f0f0]", text: "text-[#888]", border: "border-[#ccc]" };
  const pct = logged / available;
  if (pct === 0)    return { bg: "bg-white",        text: "text-[#aaa]",    border: "border-[#ddd]" };
  if (pct < 0.8)    return { bg: "bg-[#d1fae5]",    text: "text-[#065f46]", border: "border-[#6ee7b7]" };
  if (pct <= 1.0)   return { bg: "bg-[#fef3c7]",    text: "text-[#92400e]", border: "border-[#fcd34d]" };
  return              { bg: "bg-[#fee2e2]",    text: "text-[#991b1b]", border: "border-[#fca5a5]" };
}

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// ─── Component ────────────────────────────────────────────────────────────────

export default function SchedulePanel({ projectId, baProjectId, team }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const now = new Date();
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth());
  const [logs, setLogs]     = useState<HourLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [logForm, setLogForm] = useState<{ ba_name: string; hours: string; logged_date: string; note: string } | null>(null);

  const weeks    = getWeeksOfMonth(year, month);
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;

  const fetchLogs = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE}/api/tools/hour-logs?project_id=${baProjectId}&month=${monthKey}`,
        { headers: authHeader }
      );
      setLogs(data);
    } catch (err) {
      console.error("fetchLogs failed:", err);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [baProjectId, monthKey]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const handleLogHours = async () => {
    if (!logForm || !logForm.ba_name || !logForm.hours || !logForm.logged_date) return;
    try {
      const { data: saved } = await axios.post(`${BASE}/api/tools/hour-logs`, {
        project_id: baProjectId, ba_name: logForm.ba_name,
        hours: parseFloat(logForm.hours), logged_date: logForm.logged_date,
        note: logForm.note || null,
      }, { headers: authHeader });
      // Optimistic update — add to local state immediately so cards reflect new hours
      setLogs(prev => [saved, ...prev]);
      const d = new Date(logForm.logged_date + "T12:00:00");
      const newYear = d.getFullYear();
      const newMonth = d.getMonth();
      setLogForm(null);
      // Only navigate away if the logged date is in a different month
      // (useEffect will fetch logs for the new month automatically)
      if (newYear !== year || newMonth !== month) {
        setYear(newYear);
        setMonth(newMonth);
      }
    } catch (err: any) {
      alert(`Failed to save hours: ${err?.response?.data?.detail || err?.message || "unknown error"}`);
    }
  };

  const handleDeleteLog = async (logId: string) => {
    await axios.delete(`${BASE}/api/tools/hour-logs/${logId}`, { headers: authHeader });
    setLogs(prev => prev.filter(l => l.id !== logId));
  };

  const loggedInWeek = (name: string, week: { start: Date; end: Date }) =>
    logs.filter(l => l.ba_name === name && dateInRange(l.logged_date, week.start, week.end))
        .reduce((s, l) => s + Number(l.hours), 0);

  const totalLoggedThisMonth  = (name: string) => logs.filter(l => l.ba_name === name).reduce((s, l) => s + Number(l.hours), 0);
  const totalCapacityThisMonth = (member: TeamMember) => weeks.reduce((s, w) => s + availableHours(member, w.start, w.end), 0);

  if (team.length === 0) {
    return (
      <div className="p-4 font-mono">
        <div className="border-2 border-[#2d5bbf] shadow-[3px_3px_0px_#2d5bbf] bg-[#e8f0ff] px-3 py-2 text-xs font-bold text-[#2d2d2d] uppercase">
          Add team members in the Team tab first.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 font-mono">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div className="border-2 border-[#2d5bbf] shadow-[2px_2px_0px_#2d5bbf] bg-[#dce8fb] px-3 py-1.5 flex items-center gap-2">
          <span className="text-sm font-black uppercase tracking-tight">Schedule</span>
          <span className="text-[10px] font-bold text-[#2d2d2d]/60 border-l-2 border-[#2d5bbf] pl-2">
            {team.length} BA{team.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Log hours button */}
          <button
            onClick={() => setLogForm({ ba_name: team[0]?.name || "", hours: "", logged_date: new Date().toISOString().slice(0,10), note: "" })}
            className="border-2 border-[#2d5bbf] shadow-[2px_2px_0px_#2d5bbf] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all bg-white px-2 py-1 text-[10px] font-black uppercase flex items-center gap-1"
          >
            <Plus className="h-3 w-3" /> Log Hours
          </button>
          {/* Month nav */}
          <div className="flex items-center border-2 border-[#2d5bbf] shadow-[2px_2px_0px_#2d5bbf]">
            <button onClick={prevMonth} className="px-1.5 py-1 border-r-2 border-[#2d5bbf] hover:bg-[#dce8fb] transition-colors">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="text-[10px] font-black uppercase px-3 min-w-[110px] text-center">
              {MONTH_NAMES[month]} {year}
            </span>
            <button onClick={nextMonth} className="px-1.5 py-1 border-l-2 border-[#2d5bbf] hover:bg-[#dce8fb] transition-colors">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* ── LOG HOURS FORM ── */}
      {logForm && (
        <div className="border-2 border-[#2d5bbf] shadow-[3px_3px_0px_#2d5bbf] bg-white p-3 space-y-2">
          <p className="text-[10px] font-black uppercase border-b-2 border-[#2d5bbf] pb-1">Log Hours</p>
          <div className="flex gap-2 flex-wrap items-end">
            <div>
              <p className="text-[9px] font-black uppercase mb-1">BA</p>
              <select
                value={logForm.ba_name}
                onChange={e => setLogForm({ ...logForm, ba_name: e.target.value })}
                className="border-2 border-[#2d5bbf] text-xs px-2 py-1 bg-white font-bold focus:outline-none"
              >
                {team.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[9px] font-black uppercase mb-1">Hours</p>
              <Input
                type="number" min={0.5} max={24} step={0.5}
                value={logForm.hours}
                onChange={e => setLogForm({ ...logForm, hours: e.target.value })}
                className="border-2 border-[#2d5bbf] rounded-none h-7 text-xs w-20 font-bold focus-visible:ring-0"
                placeholder="4"
              />
            </div>
            <div>
              <p className="text-[9px] font-black uppercase mb-1">Date</p>
              <Input
                type="date" value={logForm.logged_date}
                onChange={e => setLogForm({ ...logForm, logged_date: e.target.value })}
                className="border-2 border-[#2d5bbf] rounded-none h-7 text-xs w-36 font-bold focus-visible:ring-0"
              />
            </div>
            <div className="flex-1 min-w-32">
              <p className="text-[9px] font-black uppercase mb-1">Note</p>
              <Input
                value={logForm.note}
                onChange={e => setLogForm({ ...logForm, note: e.target.value })}
                placeholder="Optional"
                className="border-2 border-[#2d5bbf] rounded-none h-7 text-xs font-bold focus-visible:ring-0"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleLogHours}
              disabled={!logForm.ba_name || !logForm.hours || !logForm.logged_date}
              className="border-2 border-[#2d5bbf] shadow-[2px_2px_0px_#2d5bbf] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all bg-[#dce8fb] px-3 py-1 text-[10px] font-black uppercase flex items-center gap-1 disabled:opacity-40"
            >
              <Clock className="h-3 w-3" /> Save
            </button>
            <button
              onClick={() => setLogForm(null)}
              className="border-2 border-[#2d5bbf] px-3 py-1 text-[10px] font-black uppercase hover:bg-[#f5f5f5] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── MEMBER CARDS ── */}
      {loading ? (
        <div className="border-2 border-[#2d5bbf] bg-[#dce8fb] px-3 py-2 text-xs font-black uppercase animate-pulse">
          Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {team.map((member) => {
            const logged    = totalLoggedThisMonth(member.name);
            const capacity  = totalCapacityThisMonth(member);
            const pct       = capacity > 0 ? Math.min((logged / capacity) * 100, 100) : 0;
            const overloaded = logged > capacity;
            const avgPerWeek = weeks.length > 0 ? logged / weeks.length : 0;
            const barColor  = overloaded ? "bg-[#fca5a5]" : pct >= 80 ? "bg-[#fcd34d]" : "bg-[#6ee7b7]";
            const statusBg  = overloaded ? "bg-[#fee2e2] border-[#fca5a5]" : pct >= 80 ? "bg-[#fef3c7] border-[#fcd34d]" : pct === 0 ? "bg-white border-[#ddd]" : "bg-[#d1fae5] border-[#6ee7b7]";
            const statusText = overloaded ? "text-[#991b1b]" : pct >= 80 ? "text-[#92400e]" : pct === 0 ? "text-[#aaa]" : "text-[#065f46]";
            const statusLabel = overloaded ? "Overloaded" : pct >= 80 ? "Near capacity" : pct === 0 ? "No hours logged" : "On track";
            return (
              <div key={member.id} className="border-2 border-[#2d5bbf] shadow-[2px_2px_0px_#2d5bbf] bg-white p-3 space-y-2.5">
                {/* Name + type */}
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-black text-[#2d2d2d]">{member.name}</p>
                    <p className="text-[9px] font-bold text-[#888] uppercase mt-0.5">
                      {member.employment_type.replace("_", "-")}
                    </p>
                  </div>
                  <span className={`text-[9px] font-black uppercase border px-1.5 py-0.5 ${statusBg} ${statusText}`}>
                    {statusLabel}
                  </span>
                </div>

                {/* Progress bar */}
                <div>
                  <div className="h-2 bg-[#e8f0ff] border border-[#a8c4f0] rounded-sm overflow-hidden">
                    <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex justify-between mt-1 text-[9px] font-bold text-[#555]">
                    <span>{logged.toFixed(1)}h logged</span>
                    <span>{capacity.toFixed(0)}h capacity</span>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-2 gap-2 border-t border-[#e8f0ff] pt-2">
                  <div>
                    <p className="text-[9px] font-black uppercase text-[#2d5bbf]">Baseline</p>
                    <p className="text-xs font-black text-[#2d2d2d]">{member.capacity_hours}h/wk</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black uppercase text-[#2d5bbf]">Avg this month</p>
                    <p className={`text-xs font-black ${overloaded ? "text-[#991b1b]" : "text-[#2d2d2d]"}`}>
                      {avgPerWeek.toFixed(1)}h/wk
                    </p>
                  </div>
                </div>

                {overloaded && (
                  <div className="flex items-center gap-1.5 border border-[#fca5a5] bg-[#fee2e2] px-2 py-1">
                    <AlertTriangle className="h-3 w-3 text-[#991b1b] shrink-0" />
                    <p className="text-[9px] font-black text-[#991b1b]">
                      +{(logged - capacity).toFixed(1)}h over capacity
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── HOUR LOG ── */}
      {logs.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-black uppercase border-b-2 border-[#2d5bbf] pb-1">
            Hour Log — {MONTH_NAMES[month]}
          </p>
          {logs.map(log => (
            <div key={log.id} className="border-2 border-[#2d5bbf] bg-white flex items-center gap-3 px-3 py-1.5 text-[10px]">
              <span className="font-black text-[#2d2d2d] w-24 shrink-0 truncate">{log.ba_name}</span>
              <span className="font-black text-white border-2 border-[#2d5bbf] bg-[#2d5bbf] px-1.5 py-0.5 shrink-0">
                {Number(log.hours).toFixed(1)}h
              </span>
              <span className="font-bold text-[#555]">{log.logged_date}</span>
              {log.note && <span className="text-[#888] truncate flex-1">{log.note}</span>}
              <button
                onClick={() => handleDeleteLog(log.id)}
                className="ml-auto text-[#888] hover:text-[#991b1b] transition-colors shrink-0"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
